import React, { useEffect, useRef, useState } from "react";
import * as Fa from "react-icons/fa";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import { fetchData } from "../../../Services/GlobalService";
import { GetDateFormat } from "../../../Services/GlobalFunction/GetDateFormat";
import { downloadExcel } from "../../../Services/GlobalFunction/DownLoadExcel";
import { AxiosExcel } from "../../../API/Axios";
import Loader from "../../../Components/Loader";

interface ConstructOption {
  label: string;  // Name
  value: number;  // Seq
  startDate: string;
  endDate: string;
}

const FORMAT_LIST = [
  { label: "xlsx", value: "xlsx" },
  { label: "csv",  value: "csv"  },
];

const NewAccountUpload: React.FC = () => {
  const UserId    = localStorage.getItem("UserId")?.toString();
  const getformat = GetDateFormat();

  const ConstructBaseUrl = "DBScoreCard/LBMBrandAndBrandMapping";
  const BaseUrl          = "DBScoreCard/NewAccountUpload";

  const Endpoint = {
    GetConstructList: "FetchingConstructData",
    ExportData:       "ExportAllNewCustomer",
    UploadFile:       "UploadNewSIPCustomer",
  };

  const fileUploadRef = useRef<FileUpload>(null);

  /* ── State ── */
  const [selectedConstruct, setSelectedConstruct] = useState<ConstructOption | null>(null);
  const [constructList,     setConstructList]     = useState<ConstructOption[]>([]);
  const [constructLoading,  setConstructLoading]  = useState<boolean>(false);
  const [loader,            setLoader]            = useState<boolean>(false);
  const [exportLoading,     setExportLoading]     = useState<boolean>(false);
  const [templateFormat,    setTemplateFormat]    = useState<string>("xlsx");

  /* ── Load constructs on mount (HDSSD hardcoded) ── */
  useEffect(() => {
    loadConstructList();
  }, []);

  const loadConstructList = async () => {
    setConstructLoading(true);
    try {
      const res = await fetchData(
        ConstructBaseUrl,
        Endpoint.GetConstructList,
        `ChannelType=HDSSD`
      );
      setConstructList(
        (res.Data ?? []).map((d: any) => ({
          label:     d.Name,
          value:     d.Seq,
          startDate: d.StartDate,
          endDate:   d.EndDate,
        }))
      );
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to load constructs");
    } finally {
      setConstructLoading(false);
    }
  };

  /* ── Download Template ── */
  const handleDownloadTemplate = () => {
    const headers = [{ "DB Code": null, "Cust Code": null, "PID": null }];
    downloadExcel(headers, `SIP_New_Account_Template_${getformat}.${templateFormat}`);
  };

  /* ── Export ── */
  const handleExport = async () => {
    if (!selectedConstruct) {
      toast.warn("Please select a Construct to export.");
      return;
    }
    setExportLoading(true);
    try {
      const res = await fetchData(
        BaseUrl,
        Endpoint.ExportData,
        `ConstructSeq=${selectedConstruct}`
      );
      if (res?.Data?.length > 0) {
        downloadExcel(res.Data, `SIP_New_Account_Export_${getformat}.xlsx`);
      } else {
        toast.warn("No data found.");
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to download file");
    } finally {
      setExportLoading(false);
    }
  };

  /* ── File Upload ── */
  const handleFileUpload = async (event: any) => {
    if (!selectedConstruct) {
      toast.warn("Please select a Construct before uploading.");
      fileUploadRef.current?.clear();
      return;
    }
    setLoader(true);
    try {
      const file = event.files[0];
      const formData = new FormData();
      formData.append("UserId",       UserId ?? "");
      formData.append("ConstructSeq", String(selectedConstruct));
      formData.append("FileContent",  file);

      const response = await AxiosExcel.post(
        `${BaseUrl}/${Endpoint.UploadFile}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const res = response.data;
      if (res.Status === "Success") {
        toast.success(res.Message);
        fileUploadRef.current?.clear();
      } else if (res.Status === "ErrorFR") {
        toast.warn(res.message ?? res.Message);
        fileUploadRef.current?.clear();
      } else if (res.Status === "Error") {
        toast.warn(res.message ?? res.Message);
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.message ?? "Upload failed");
      fileUploadRef.current?.clear();
    } finally {
      setLoader(false);
    }
  };

  if (loader) return <Loader />;

  return (
    <main>
      <div className="col-12 mb-5">

        {/* ── Breadcrumb ── */}
        <div className="col-lg-12 col-md-12 col-sm-12">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mx-0">
              <li className="breadcrumb-item">DB SIP</li>
              <li className="breadcrumb-item active" aria-current="page">
                New Account Upload - HDSSD
              </li>
            </ol>
          </nav>
        </div>

        {/* ── Page title + action buttons ── */}
       <div className="row">
                 <div className="col-lg-4 col-md-12 col-sm-12">
                   <h3 className="py-2">New Account Upload</h3>
                 </div>
                 <div className="col-lg-8 col-md-12 col-sm-12">
                   <div className="text-end">
                     <div className="d-inline-flex">
                       <Dropdown
                         value={templateFormat}
                         onChange={(e) => setTemplateFormat(e.value)}
                         options={FORMAT_LIST}
                         optionLabel="label"
                         style={{
                           width: 100,
                           borderRadius: "0 6px 6px 0",
                           border: "1px solid #ced4da",
                           borderLeft: "none",
                           height: "100%",
                         }}
                       />
                     </div>
                     <button
                       type="button"
                       className="btn anime-secondary-btn mx-2"
                       onClick={handleDownloadTemplate}
                     >
                       Download Template <Fa.FaDownload className="ms-2" />
                     </button>
                     <button
                       type="button"
                       className="btn anime-btn"
                       onClick={handleExport}
                       disabled={exportLoading}
                     >
                       {exportLoading ? (
                         <>
                           Exporting...{" "}
                           <span className="spinner-border spinner-border-sm ms-1" role="status" aria-hidden="true" />
                         </>
                       ) : (
                         <>Export <Fa.FaFileExport className="ms-2" /></>
                       )}
                     </button>
                   </div>
                 </div>
               </div>

        <section className="sales-box">
          <div className="row d-flex justify-content-end align-items-end">
            <div className="col-lg-7 col-md-7 col-sm-12">

                {/* Construct dropdown */}
                <div className="col-lg-6 col-md-12 col-sm-12 mb-3">
                  <div className="d-block">
                    <label className="form-label">Construct Name</label>
                    <Dropdown
                      value={selectedConstruct}
                      onChange={(e) => setSelectedConstruct(e.value)}
                      options={constructList}
                      optionLabel="label"
                      placeholder={constructLoading ? "Loading..." : "Select Construct"}
                      disabled={constructLoading}
                      className="w-100"
                    />
                  </div>
                </div>

                {/* Upload section — accepts xlsx, xls and csv */}
                <div className="col-lg-12 col-md-12 col-sm-12 mt-2">
                  <div className="UploadSection clearfix">
                    <div className="conText">
                      <Fa.FaInfoCircle className="icon" /> Select an Excel or CSV File to upload
                    </div>
                    <FileUpload
                      ref={fileUploadRef}
                      className="clearfix nTopMargin"
                      name="demo[]"
                      accept=".xlsx, .xls, .csv"
                      emptyTemplate={
                        <p className="m-0">Drag and drop files to here to upload.</p>
                      }
                      customUpload
                      uploadHandler={handleFileUpload}
                    />
                  </div>
                </div>
            </div>

            <div className="col-lg-5 col-md-5 col-sm-12">
              <div className="text-end">
                <img
                  src={require("../../../Assets/Images/File_Upload.png")}
                  className="File_Upload_Img drop-right"
                  alt="InnoRise"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default NewAccountUpload;